// components/Auth/GoogleAuth.tsx
"use client";

import { handleAuthResponse } from "@/lib/auth-handler";
import { isMobileDevice } from "@/utils";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { useRouter } from "next/navigation";
import { useState } from "react";
import toast from "react-hot-toast";

interface GoogleAuthProps {
  action: "login" | "register";
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

export const GoogleAuth = ({ action, onSuccess, onError }: GoogleAuthProps) => {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);

  const handleSuccess = async (credentialResponse: any) => {
    try {
      setError(null);
      const endpoint = action === "login" ? "/api/login" : "/api/register";

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          googleToken: credentialResponse?.credential,
          isMobile: isMobileDevice(),
        }),
        cache: "no-cache",
      });

      const data = await response.json();

      if (response?.ok) {
        await handleAuthResponse(data);
        toast.success(
          `${action === "login" ? "Login Successful !" : "Signup Successful !"}`
        );
        router.push("/dashboard/");
      } else {
        const errorMsg = data?.message || `${action} failed`;
        setError(errorMsg);
        if (onError) onError(errorMsg);
      }
    } catch (err) {
      const errorMsg = "An error occurred. Please try again.";
      setError(errorMsg);
      if (onError) onError(errorMsg);
    }
  };

  return (
    <div className="mb-6 max-w-fit mx-auto">
      <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID!}>
        <GoogleLogin
          onSuccess={handleSuccess}
          onError={() => {
            const errorMsg = "Google authentication failed";
            setError(errorMsg);
            if (onError) onError(errorMsg);
          }}
          useOneTap
          text={action === "login" ? "signin_with" : "signup_with"}
          shape="rectangular"
          size="large"
          width="100%"
        />
      </GoogleOAuthProvider>
      {error && (
        <p className="mt-2 text-center text-sm text-red-500">{error}</p>
      )}
    </div>
  );
};

export const DividerWithText = ({ text }: { text: string }) => (
  <div className="relative my-6">
    <div className="absolute inset-0 flex items-center">
      <div className="w-full border-t border-stroke dark:border-dark-3"></div>
    </div>
    <div className="relative flex justify-center text-sm">
      <span className="bg-white px-2 text-dark-4 dark:bg-gray-dark dark:text-dark-6">
        {text}
      </span>
    </div>
  </div>
);
